import React, { useContext, useState } from "react";
import { Modal } from "react-bootstrap";
import { Button,  ThemeProviderV2} from '@blueprint/ui';
import { useModal } from "react-modal-hook";

import invoiceApi from "../../../api/invoice";
import { Loader, toast } from "../../../common/components/core";
import { useAuthErrorHandlerCreator } from "../../../common/hooks";
import { Handler } from "../../../types/common";
import { shortenUUID } from "../../../utils/common";
import {
    composeErrorHandlers,
    GenericError,
    onStaleStateError,
} from "../../../utils/errorHandling";
import InvoiceContext from "../InvoiceContext";

const useContractorDiscardInvoiceHandler = (invoiceRef: string): Handler => {
    const [isSubmitting, setSubmitting] = useState(false);
    const onAuthError = useAuthErrorHandlerCreator();
    const { mutate } = useContext(InvoiceContext);

    const [show, hide] = useModal(() => {
        const confirm = async () => {
            setSubmitting(true);
            try {
                const result = await invoiceApi.discard(invoiceRef);
                mutate(result.stateAndRefMap.InvoiceState[0]);
                const newInvoiceId =
                    result.stateAndRefMap.InvoiceState?.[0]?.state.data.linearId
                        .id;
                toast(`Invoice #${shortenUUID(newInvoiceId ?? "")} discarded`);
                hide();
            } catch (e) {
                composeErrorHandlers(
                    onStaleStateError(() => {
                        mutate();
                        hide();
                        toast("Page has been updated by another user.");
                    }),
                    onAuthError,
                )(e as GenericError);
            }
            setSubmitting(false);
        };

        return (
            <ThemeProviderV2>
            <Modal show onHide={hide} backdrop="static" centered>
                <Loader overlay isLoading={isSubmitting}>
                    <Modal.Header>
                        <h2 className="mb-0">
                            Discard Invoice Submission
                        </h2>
                    </Modal.Header>
                    <Modal.Body>
                        <p>
                            Are you sure you want to discard this invoice
                            submission?
                        </p>
                    </Modal.Body>

                    <Modal.Footer className="d-flex justify-content-between px-3">
                        <Button variant="light.tertiary" onClick={hide}>
                            Cancel
                        </Button>
                        <Button variant="light.primary" onClick={confirm}>
                            Confirm
                        </Button>
                    </Modal.Footer>
                </Loader>
            </Modal>
            </ThemeProviderV2>
        );
    }, [invoiceRef, isSubmitting, mutate, onAuthError]);

    return {
        isSubmitting,
        handler: show,
    };
};

export default useContractorDiscardInvoiceHandler;
